import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Globe } from "lucide-react";
import { useLang } from "../i18n";
import { buildWhatsAppLink, CLIENT_AREA_URL } from "../lib/constants";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -80 });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "home", label: t.nav.home },
    { id: "servicos", label: t.nav.services },
    { id: "sobre", label: t.nav.about },
    { id: "clientes", label: t.nav.clients },
    { id: "contato", label: t.nav.contact },
  ];

  const consultLink = buildWhatsAppLink(t.hero.whatsappMsg);

  const handleNav = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "bg-[#050507]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <button
            data-testid="header-logo"
            onClick={() => handleNav("home")}
            className="flex items-center gap-2 shrink-0"
            aria-label="Waltech - Home"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/waltech-logo.png`}
              alt="Waltech"
              className="h-9 w-auto object-contain"
            />
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Principal">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="relative px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors duration-300 group"
              >
                {l.label}
                <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[#00e5ff] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              </button>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="lang-toggle"
              onClick={toggle}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-xs font-semibold text-zinc-300 hover:text-white hover:border-white/30 transition-colors duration-300 font-mono-wt uppercase"
              aria-label="Toggle language"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === "pt" ? "PT" : "EN"}
            </button>

            <a
              data-testid="header-client-area"
              href={CLIENT_AREA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[#00e5ff]/40 text-sm font-medium text-[#00e5ff] hover:bg-[#00e5ff]/10 transition-colors duration-300"
            >
              {t.nav.clientArea}
              <ArrowUpRight className="h-4 w-4" />
            </a>

            <a
              data-testid="header-consult-cta"
              href={consultLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-[#0047ff] text-sm font-semibold text-white hover:bg-[#2b5bff] transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_24px_rgba(0,71,255,0.5)]"
            >
              {t.nav.consult}
            </a>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-white/10 text-white"
              aria-label="Menu"
              aria-expanded={open}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#050507]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => handleNav(l.id)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="text-left py-3 text-lg font-medium text-zinc-200 border-b border-white/5"
                >
                  {l.label}
                </motion.button>
              ))}

              <div className="mt-4 flex items-center gap-3">
                <button
                  data-testid="mobile-lang-toggle"
                  onClick={toggle}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-white/15 text-sm font-semibold text-white font-mono-wt uppercase"
                >
                  <Globe className="h-4 w-4" />
                  {lang === "pt" ? "PT" : "EN"}
                </button>
                <a
                  data-testid="mobile-client-area"
                  href={CLIENT_AREA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-full border border-[#00e5ff]/40 text-sm font-medium text-[#00e5ff]"
                >
                  {t.nav.clientArea}
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>

              <a
                data-testid="mobile-consult-cta"
                href={consultLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#0047ff] text-base font-semibold text-white"
              >
                {t.nav.consult}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export { scrollTo };
